import { makeStyles } from '@material-ui/core';
import OrganizationIcon from '@material-ui/icons/Business';
import React, { useEffect, useState, useMemo } from 'react';
import {
  Datagrid,
  List,
  ReferenceManyField,
  Responsive,
  TextInput,
  SelectInput,
  SimpleList,
  TextField,
  TopToolbar,
  Pagination,
  useGetList,
  useListContext
} from 'react-admin';

import BulkActionDeleteButton from '../../core/components/BulkActionDeleteButton';
import LinkField from '../../core/components/LinkField';
import Empty from '../../core/components/list/Empty';
import Filter from '../../core/components/list/StyledFilter';
import ListTitleBar from './importorganizations/ExcelOrganizationImportTitleBarWithDropbox';
import StatusSelectField from '../../core/components/StatusSelectField';
import {
  predictiveSearchFormatter,
  predictiveSearchParser
} from '../../core/utils';
import { organizationStatus } from './OrganizationLookups';
import OrganizationInvestorDatagrid from './organizationsinvestor/OrganizationInvestorDatagrid';
import AutocompleteInput from '../../core/components/forked/input/AutocompleteInput';

const useStyles = makeStyles({
  expandOnlyDataGridHead: {
    '& tr': {
      '& th:nth-of-type(1)': {
        width: '14px'
      }
    }
  },
  expandDataGridHead: {
    '& tr': {
      '& th:nth-of-type(1)': {
        width: '14px'
      },
      '& th:nth-of-type(2)': {
        width: '24px'
      }
    }
  },
  expandIconCell: {
    width: '14px',
    '& div': {
      width: '14px',
      padding: '0'
    },
    '& div:hover ': {
      backgroundColor: 'transparent'
    }
  },
  statusColumnStyle: {
    width: '68px'
  },
  root: {
    marginRight: 'auto'
  }
});

const useshowStyles = makeStyles({
  root: {
    padding: '16px 16px 10px 16px'
  }
});

const Actions = ({
  displayedFilters,
  filters,
  filterValues,
  resource,
  showFilter
}) => {
  const classes = useStyles();

  return (
    <TopToolbar classes={{ root: classes.root }}>
      {filters &&
        React.cloneElement(filters, {
          resource,
          showFilter,
          displayedFilters,
          filterValues,
          context: 'button'
        })}
    </TopToolbar>
  );
};

const OrganizationFilter = ({ filterValues, setAppliedFilters, ...props }) => {
  useEffect(() => {
    if (filterValues) {
      setAppliedFilters(filterValues);
    }
  }, [filterValues, setAppliedFilters]);

  const { data } = useGetList('fundfamilies', { perPage: -1 }, nameSort, null);

  const fundFamilies = useMemo(() => Object.values(data), [data]);

  return (
    <Filter variant="standard" {...props}>
      <TextInput
        label="Search by Family Name"
        source="name"
        alwaysOn
        parse={predictiveSearchParser}
        format={predictiveSearchFormatter}
        options={{ autoComplete: 'off' }}
      />
      <TextInput
        label="A.K.A."
        source="aka"
        allowEmpty
        parse={predictiveSearchParser}
        format={predictiveSearchFormatter}
        options={{ autoComplete: 'off' }}
      />
      <TextInput
        label="Family ID"
        source="id"
        allowEmpty
        parse={predictiveSearchParser}
        format={predictiveSearchFormatter}
        options={{ autoComplete: 'off' }}
      />
      <SelectInput
        label="Status"
        source="status"
        choices={organizationStatus}
        allowEmpty
      />
      {fundFamilies && fundFamilies.length > 0 && (
        <AutocompleteInput
          source="fundFamilyId"
          label="Fund Families"
          optionText="name"
          autoComplete="off"
          showArrowDown={true}
          clientSideFilterOnly={true}
          choices={fundFamilies}
          alwaysOn
          emptyText="All Fund Families"
          placeholder="All Fund Families"
        />
      )}
    </Filter>
  );
};

const OrganizationShow = props => {
  const classes = useshowStyles();

  const handleRowClick = investorId => {
    return `/investors/${investorId}`;
  };

  const { permissions, ...rest } = props;

  return (
    <div className={classes.root}>
      <ReferenceManyField
        label=""
        reference="investors"
        target="organizationId"
        //filter={{ orderBy: 'name', orderDirection: 'ASC' }}
        sort={nameSort}
        {...rest}
      >
        <OrganizationInvestorDatagrid
          handleRowClick={handleRowClick}
          expand={true}
          permissions={permissions}
        />
      </ReferenceManyField>
    </div>
  );
};

const OrganizationTitle = props => {
  return (
    <ListTitleBar
      type="Investor Families"
      title="Create New Investor Family"
      createUrl="#/organizations/create"
      icon={<OrganizationIcon />}
      hasCreatePermission={props.hasCreatePermission}
      hasImportOrganizationExcelPermission={
        props.hasImportOrganizationExcelPermission
      }
      hasExportOrganizationsExcelPermission={
        props.hasExportOrganizationsExcelPermission
      }
      selectedIds={props.selectedIds}
      filters={props.filters}
    />
  );
};

const nameSort = { field: 'name', order: 'ASC' };
const organizationFilter = { orphanVisibility: 'ALL_VISIBLE' };

const OrganizationList = props => {
  const classes = useStyles();
  const [appliedFilters, setAppliedFilters] = useState([]);
  const {
    permissions = [],
    hasCreatePermission,
    hasEditPermission,
    hasDeletePermission,
    userProofile,
    ...rest
  } = props;
  const [selectedIds, setSelectedIds] = useState([]);

  const [
    hasImportOrganizationExcelPermission,
    hasExportOrganizationsExcelPermission
  ] = useMemo(
    () =>
      props.permissions
        ? [
            props.permissions.includes(
              'AUTH_IMPORT_INVESTOR_FAMILIES_FROM_EXCEL'
            ),
            props.permissions.includes('AUTH_EXPORT_INVESTOR_ORGANIZATION')
          ]
        : [false, false],
    [props.permissions]
  );

  return (
    <List
      {...rest}
      sort={nameSort}
      filter={organizationFilter}
      exporter={false}
      title={
        <OrganizationTitle
          hasCreatePermission={hasCreatePermission}
          hasExportOrganizationsExcelPermission={
            hasExportOrganizationsExcelPermission
          }
          hasImportOrganizationExcelPermission={
            hasImportOrganizationExcelPermission
          }
          filters={appliedFilters}
          selectedIds={selectedIds}
        />
      }
      filters={<OrganizationFilter setAppliedFilters={setAppliedFilters} />}
      perPage={20}
      pagination={<Pagination rowsPerPageOptions={[20, 25, 30]} />}
      bulkActionButtons={hasDeletePermission && <BulkActionDeleteButton />}
      actions={<Actions />}
      empty={<Empty />}
      syncWithLocation
    >
      <Responsive
        small={
          <SimpleList
            primaryText={record => record.name}
            secondaryText={record => record.id}
            tertiaryText={() => ''}
          />
        }
        medium={
          <DatagridWithListContext
            hasDeletePermission={hasDeletePermission}
            permissions={permissions}
            classes={classes}
            setSelectedIds={setSelectedIds}
            {...rest}
          />
        }
      />
    </List>
  );
};
const DatagridWithListContext = props => {
  const {
    hasDeletePermission,
    permissions,
    classes,
    setSelectedIds,
    ...dataGridProps
  } = props;
  const { selectedIds } = useListContext(props);

  useEffect(() => {
    setSelectedIds(selectedIds);
  }, [selectedIds, setSelectedIds]);

  return (
    <>
      <Datagrid
        {...dataGridProps}
        classes={{
          thead: hasDeletePermission
            ? classes.expandDataGridHead
            : classes.expandOnlyDataGridHead,
          expandIconCell: classes.expandIconCell
        }}
        expand={<OrganizationShow permissions={permissions} />}
      >
        <LinkField label="Family Name" source="name" id="organization name" />
        <TextField label="A.K.A." source="aka" id="organization aka" />
        <TextField label="Family ID" source="id" id="organization id" />
        <StatusSelectField
          label="Status"
          source="status"
          choices={organizationStatus}
          id="organization status"
          headerClassName={classes.statusColumnStyle}
        />
      </Datagrid>
    </>
  );
};

export default OrganizationList;
